<template>
  <b-modal
    ref="modal"
    :title="$t('influencer_panel.generate_link_title')"
    :ok-title="$t('influencer_panel.generate_link')"
    :cancel-title="$t('action.cancel')"
    cancel-variant="outline-secondary"
    :ok-disabled="!selectedParticipation || saving || selectedProduct === ''"
    @ok.prevent="submit"
    @hidden="reset"
  >
    <b-form-group :label="$t('influencer_panel.link_campaign')">
      <b-form-select v-model="selectedParticipation" :options="participationOptions" />
    </b-form-group>

    <b-form-group
      v-if="productOptions.length > 1"
      :label="$t('influencer_panel.link_scope')"
      :description="selectedProduct === null ? $t('influencer_panel.whole_campaign_hint') : ''"
    >
      <b-form-select v-model="selectedProduct" :options="productOptions" />
    </b-form-group>

    <!-- O canal é o que faz "de onde vêm suas vendas" existir: um link por rede, e a
         venda volta sabendo de onde veio. -->
    <b-form-group :label="$t('influencer_panel.link_channel')" :description="$t('influencer_panel.link_channel_hint')">
      <b-form-select v-model="selectedChannel" :options="channelOptions" />
    </b-form-group>

    <div v-if="activeCoupon" class="generate-link__coupon">
      <div class="generate-link__coupon-row">
        <feather-icon icon="TagIcon" size="14" />
        <code class="generate-link__coupon-code">{{ activeCoupon.code }}</code>
        <b-badge v-if="couponDiscount" variant="light-primary">
          {{ $t('influencer_panel.coupon_discount', { discount: couponDiscount }) }}
        </b-badge>
        <hb-copy-button :text="activeCoupon.code" :allow-share="false" class="ml-auto" />
      </div>
      <p class="generate-link__coupon-note">
        {{ activeCoupon.active
          ? $t('influencer_panel.coupon_auto_applied')
          : $t('influencer_panel.coupon_inactive') }}
      </p>
    </div>

    <div v-if="createdLink" class="generate-link__result">
      <div class="generate-link__result-label">{{ $t('influencer_panel.link_created') }}</div>
      <div class="generate-link__result-row">
        <code class="generate-link__url">{{ createdLink.short_url }}</code>
        <hb-copy-button :text="createdLink.short_url" />
      </div>
      <div v-if="activeCoupon && activeCoupon.active" class="generate-link__result-coupon">
        {{ $t('influencer_panel.link_coupon_note', { code: activeCoupon.code }) }}
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BFormGroup, BFormSelect, BBadge,
} from 'bootstrap-vue'
import InfluencerPanelService from '@/services/api/InfluencerPanelService'
import HbCopyButton from '@/components/hubee/HbCopyButton.vue'
import { campaignName, discountLabel } from '../campaign'
import { extractMessage } from '../panel'

/**
 * Gera um link rastreavel para uma campanha, apontando para um produto dela.
 *
 * O endpoint (`POST /me/participations/{id}/links`) e o metodo de service existiam desde o
 * inicio e nunca tiveram tela — este e o pedaco que faltava.
 *
 * A pagina do produto e o destino padrao (1 produto auto-seleciona; varios exigem
 * escolha): e o link que converte. "Campanha inteira" continua possivel e leva a
 * pagina do clube. O cupom da participacao aparece aqui porque o link o aplica
 * sozinho no checkout — quem divulga precisa saber o que o seguidor vai receber.
 */
export default {
  name: 'GenerateLinkModal',
  components: {
    BModal, BFormGroup, BFormSelect, BBadge, HbCopyButton,
  },
  props: {
    participations: { type: Array, default: () => [] },
    /** Id de participacao pre-selecionado, quando aberto a partir de uma campanha. */
    preselected: { type: Number, default: null },
  },
  data: () => ({
    service: null,
    selectedParticipation: null,
    selectedProduct: null,
    selectedChannel: null,
    createdLink: null,
    saving: false,
  }),
  computed: {
    /** Os códigos são os de `TrackingLink::CHANNEL_LABELS` no backend. */
    channelOptions() {
      return [
        { value: null, text: this.$t('influencer_panel.channel_none') },
        { value: 'IG', text: 'Instagram' },
        { value: 'BI', text: this.$t('influencer_panel.channel_bio') },
        { value: 'WA', text: 'WhatsApp' },
        { value: 'TT', text: 'TikTok' },
        { value: 'YT', text: 'YouTube' },
        { value: 'OT', text: this.$t('influencer_panel.channel_other') },
      ]
    },
    participationOptions() {
      return this.participations.map(p => ({ value: p.id, text: campaignName(p) }))
    },
    selectedParticipationObj() {
      return this.participations.find(p => p.id === this.selectedParticipation) || null
    },
    productOptions() {
      const products = this.productsOf(this.selectedParticipation)

      const options = [
        { value: null, text: this.$t('influencer_panel.whole_campaign') },
        ...products.map(item => ({
          value: item.product_id || item.id,
          text: (item.product && item.product.name) || item.name || `#${item.product_id || item.id}`,
        })),
      ]

      if (products.length > 1) {
        options.unshift({ value: '', text: this.$t('influencer_panel.choose_destination'), disabled: true })
      }

      return options
    },
    /**
     * Cupom da participacao selecionada. `coupon_code` existe mesmo com a relacao
     * `coupon` ausente do payload; nesse caso assume ativo — o backend nao anexa
     * `hb_coupon` de cupom pausado/vencido de qualquer forma.
     */
    activeCoupon() {
      const p = this.selectedParticipationObj
      if (!p || !p.coupon_code) return null

      const active = !p.coupon || p.coupon.status === 'AC'

      return { code: p.coupon_code, active }
    },
    couponDiscount() {
      const p = this.selectedParticipationObj

      return p ? discountLabel(p.campaign) : ''
    },
  },
  watch: {
    selectedParticipation() {
      this.applyDefaultProduct()
    },
  },
  beforeMount() {
    this.service = InfluencerPanelService.build(this.$vs)
  },
  methods: {
    /**
     * O id vem por argumento porque o pai seta a prop `preselected` e chama
     * open() no mesmo tick — a prop ainda nao propagou nesse momento, e o
     * modal abria sempre na primeira campanha da lista.
     */
    open(participationId = null) {
      this.selectedParticipation = participationId
        || this.preselected
        || (this.participationOptions[0] || {}).value
        || null
      // O watcher nao dispara quando a participacao reaberta e a mesma.
      this.applyDefaultProduct()
      this.$refs.modal.show()
    },
    productsOf(participationId) {
      const participation = this.participations.find(p => p.id === participationId)

      return (participation && participation.campaign && participation.campaign.products) || []
    },
    applyDefaultProduct() {
      const products = this.productsOf(this.selectedParticipation)

      if (products.length === 1) {
        this.selectedProduct = products[0].product_id || products[0].id
      } else if (products.length > 1) {
        this.selectedProduct = ''
      } else {
        this.selectedProduct = null
      }
    },
    reset() {
      this.createdLink = null
      this.selectedProduct = null
      this.selectedChannel = null
      this.saving = false
    },
    submit() {
      this.saving = true

      this.$vs.loading()
      this.service.createLink(this.selectedParticipation, this.selectedProduct, this.selectedChannel)
        .then(link => {
          this.createdLink = link
          this.notifySuccess(this.$vs, this.$t('influencer_panel.link_created'))
          this.$emit('created', link)
        })
        .catch(error => {
          this.$vs.notify({ text: extractMessage(error), color: 'danger' })
        })
        .finally(() => {
          this.saving = false
          this.$vs.loading.close()
        })
    },
  },
}
</script>

<style scoped>
.generate-link__coupon {
  margin-top: 0.25rem;
  padding: 0.85rem 1rem;
  background: var(--surface-sunken);
  border-radius: var(--radius);
}

.generate-link__coupon-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.generate-link__coupon-code {
  font-weight: var(--fw-medium);
}

.generate-link__coupon-note {
  margin: 0.5rem 0 0;
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.generate-link__result {
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  background: var(--tint-success);
  border-radius: var(--radius);
}

.generate-link__result-coupon {
  margin-top: 0.5rem;
  font-size: var(--fs-sm);
}

.generate-link__result-label {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  margin-bottom: 0.35rem;
}

.generate-link__result-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.generate-link__url {
  word-break: break-all;
}
</style>
